import React from 'react';

import { MAX_RETRY_VALUE } from 'constants/constants';
import { wait } from 'src/utils/commonUtil';

type ComponentModule<T extends React.ComponentType<any>> = {
  default: T;
};

const CHUNK_LOAD_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Loading chunk',
  'Failed to fetch dynamically imported module',
];

const hasChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

const importChunkWithRetry = async <T extends React.ComponentType<any>>(
  importer: () => Promise<ComponentModule<T>>,
  retriesLeft: number,
): Promise<ComponentModule<T>> => {
  try {
    return await importer();
  } catch (error) {
    if (retriesLeft <= 0) {
      throw error;
    }

    await wait(1000);

    return importChunkWithRetry(importer, retriesLeft - 1);
  }
};

export function lazyWithRetry<T extends React.ComponentType<any>>(
  importer: () => Promise<ComponentModule<T>>,
  cacheKey: string,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const module = await importChunkWithRetry(
        importer,
        MAX_RETRY_VALUE,
      );

      sessionStorage.removeItem(cacheKey);

      return module;
    } catch (error) {
      const hasRetriedRefresh = sessionStorage.getItem(cacheKey) === 'true';

      if (hasChunkLoadError(error) && !hasRetriedRefresh) {
        sessionStorage.setItem(cacheKey, 'true');
        window.location.reload();

        return new Promise<ComponentModule<T>>(() => {});
      }

      sessionStorage.removeItem(cacheKey);
      throw error;
    }
  });
}
